import { useState } from 'react';
import {
  createFileRoute,
  useNavigate,
  useLocation,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Plus, Loader2 } from 'lucide-react';
import { getAbsenceTypes, type AbsenceType } from '@/lib/absences';
import { normalizeRoutePath } from '@/lib/utils';
import { CreateAbsenceTypeDialog, AbsenceTypeCard } from '@/features/absences';

const routePath = '/settings/absence-types' as const;
export const Route = createFileRoute(routePath)({
  // The list is a tab of the Settings page, mounted from `?tab=`; a direct hit on the
  // legacy list path bounces there before this route loads, so the page never mounts
  // (and never fetches) on the way through. The configure drill-in loads as usual.
  beforeLoad: ({ location }) => {
    if (normalizeRoutePath(location.pathname) === normalizeRoutePath(routePath)) {
      throw redirect({ to: '/settings', search: { tab: 'absence-types' } });
    }
  },
  component: AbsenceTypesPage,
});

export function AbsenceTypesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // The configure drill-in (/settings/absence-types/:id/configure) renders via Outlet.
  const hasChildRoute = normalizeRoutePath(location.pathname).endsWith('/configure');

  const {
    data: absenceTypes = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['absence-types'],
    queryFn: getAbsenceTypes,
    enabled: !hasChildRoute, // Only fetch when showing the list
  });

  const handleConfigure = (absenceType: AbsenceType) => {
    navigate({
      to: '/settings/absence-types/$id/configure',
      params: { id: absenceType.id },
    });
  };

  // The configure drill-in renders via Outlet.
  if (hasChildRoute) {
    return <Outlet />;
  }

  return (
    <div className="flex-1 overflow-auto">
      <div className="p-6 max-w-5xl mx-auto space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground mb-2">
              Absence Types
            </h1>
            <p className="text-sm text-muted-foreground">
              Configure leave types and absence categories
            </p>
          </div>
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  className="h-9 w-9 shrink-0 cursor-pointer rounded-full"
                  onClick={() => setCreateDialogOpen(true)}
                  aria-label="Create absence type"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Create absence type</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Absence Types List */}
        {error ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-sm text-destructive mb-4">
                Error loading absence types:{' '}
                {error instanceof Error ? error.message : 'Unknown error'}
              </p>
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                className="gap-2 cursor-pointer"
              >
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : absenceTypes.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <div className="rounded-full bg-muted p-3 mb-4">
                <Plus className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium text-foreground mb-1">No absence types yet</p>
              <p className="text-sm text-muted-foreground mb-4">
                Get started by creating your first absence type
              </p>
              <Button
                onClick={() => setCreateDialogOpen(true)}
                variant="outline"
                className="gap-2 cursor-pointer"
              >
                <Plus className="h-4 w-4" />
                Create Absence Type
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {absenceTypes.map((absenceType) => (
              <AbsenceTypeCard
                key={absenceType.id}
                absenceType={absenceType}
                onConfigure={handleConfigure}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <CreateAbsenceTypeDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} />
    </div>
  );
}
